
'use client';

import { useState, useEffect, useMemo, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { GradientText } from '@/components/ui/gradient-text';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Search, Calendar, User, Clock } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import Image from 'next/image';
import Link from 'next/link';

const POSTS_PER_PAGE = 5;

interface BlogPost {
  id: number;
  title: string;
  content: string;
  author: string;
  source: string;
  date: string;
  image_url?: string;
  reading_time?: number;
}

interface BlogPostsData {
    posts: BlogPost[];
}

const API_BASE_URL = process.env.NEXT_PUBLIC_BACKEND_API_URL || 'https://api.keerainnovations.com';

async function fetchBlogPosts(): Promise<{
  success: boolean;
  data?: BlogPostsData;
  message?: string;
}> {
  try {
    const url = `${API_BASE_URL}/api/blogs`;
      
    const response = await fetch(url, { next: { revalidate: 600 } });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred during fetch.' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return { success: true, data: data };
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
    return { success: false, message: `Failed to fetch blog posts: ${errorMessage}` };
  }
}

function BlogContent() {
  const [initialPosts, setInitialPosts] = useState<BlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const searchParams = useSearchParams();
  const page = searchParams.get('page') ?? '1';
  const currentPage = Number(page);

  useEffect(() => {
    async function loadPosts() {
      setIsLoading(true);
      setError(null);
      try {
        const result = await fetchBlogPosts();
        if (result.success && result.data) {
          const sortedPosts = result.data.posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
          setInitialPosts(sortedPosts);
          setFilteredPosts(sortedPosts);
        } else {
          setError(result.message || 'Could not load blog posts.');
        }
      } catch (e) {
        setError('An unexpected error occurred.');
      }
      setIsLoading(false);
    }
    loadPosts();
  }, []);

  useEffect(() => {
    const lowercasedFilter = searchTerm.toLowerCase();
    const filtered = initialPosts.filter(post =>
      post.title.toLowerCase().includes(lowercasedFilter) ||
      post.content.toLowerCase().includes(lowercasedFilter)
    );
    setFilteredPosts(filtered);
    // Resetting to page 1 is implicitly handled by URL change in search input
  }, [searchTerm, initialPosts]);

  const paginatedPosts = useMemo(() => {
    const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
    return filteredPosts.slice(startIndex, startIndex + POSTS_PER_PAGE);
  }, [filteredPosts, currentPage]);

  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);

  return (
    <>
      <div className="mb-8 max-w-lg mx-auto">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search blog posts..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="text-center text-destructive p-8 bg-destructive/10 rounded-lg">
          <p className='font-bold text-lg mb-2'>Error loading posts</p>
          <p className='text-sm'>{error}</p>
        </div>
      ) : (
        <>
          {filteredPosts.length > 0 ? (
            <div className="grid gap-12">
              {paginatedPosts.map((post) => (
                <Card key={post.id} className="overflow-hidden">
                    {post.image_url && (
                        <Link href={`/blogs/${post.id}`}>
                            <Image
                                src={post.image_url}
                                alt={post.title}
                                width={800}
                                height={400}
                                className="w-full h-64 object-cover cursor-pointer transition-transform hover:scale-105 duration-300"
                                data-ai-hint="blog post image"
                            />
                        </Link>
                    )}
                    <CardHeader>
                        <CardTitle className="font-headline text-3xl">
                            <Link href={`/blogs/${post.id}`} className="hover:text-primary transition-colors">
                                {post.title}
                            </Link>
                        </CardTitle>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground pt-2">
                            <div className="flex items-center gap-2">
                                <User className="h-4 w-4" />
                                <span>{post.author}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                <time dateTime={post.date}>
                                    {format(parseISO(post.date), 'MMMM dd, yyyy')}
                                </time>
                            </div>
                            {post.reading_time && (
                                <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4" />
                                    <span>{post.reading_time} min read</span>
                                </div>
                            )}
                        </div>
                    </CardHeader>
                  <CardContent>
                    <div
                      className="prose prose-invert prose-lg max-w-none line-clamp-3"
                      dangerouslySetInnerHTML={{ __html: post.content }}
                    />
                    <div className="mt-4">
                        <Button asChild variant="link" className="p-0 h-auto">
                            <Link href={`/blogs/${post.id}`}>Read More</Link>
                        </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
             <div className="text-center text-muted-foreground py-16">
              <p className="text-lg">No posts found. New content is coming soon!</p>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-12 space-x-4">
              <Button 
                variant="outline" 
                disabled={currentPage === 1}
              >
                <Link href={{ pathname: '/blogs', query: { page: currentPage - 1 } }}>Previous</Link>
              </Button>
              <span className="text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <Button 
                variant="outline"
                disabled={currentPage === totalPages}
              >
                <Link href={{ pathname: '/blogs', query: { page: currentPage + 1 } }}>Next</Link>
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
}

export default function BlogsPage() {
  return (
    <div className="container mx-auto max-w-5xl py-12 px-4">
      <div className="text-center mb-12">
        <h1 className="font-headline text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
          Our <GradientText>Blog</GradientText>
        </h1>
        <p className="mt-4 max-w-2xl mx-auto text-lg text-muted-foreground">
          Insights and articles from the KeeRa Innovations team.
        </p>
      </div>
      <Suspense fallback={<div className="flex justify-center items-center h-64"><Loader2 className="h-12 w-12 animate-spin text-primary" /></div>}>
        <BlogContent />
      </Suspense>
    </div>
  );
}
